import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslocoModule } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { SubscriptionService } from 'app/modules/partner/services/subscription.service';
import { Subscription } from 'app/modules/shared/models/subscription';
import { SubscriptionStatusEnum } from 'app/modules/shared/enums/subscriptionStatusEnum';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';

/**
 * Payment Failed Banner.
 *
 * Persistent, full-width warning banner shown to PartnerUsers whose
 * subscription is in PAYMENT_INCOMPLETE (PIN) status — i.e. Stripe is
 * currently retrying a failed payment (Smart Retries grace period).
 *
 * Scope:
 *  - Only renders when subscription.subscriptionStatusRefCode === 'PIN'
 *  - Hidden during SUSPENDED, EXPIRED, CANCELED, or post-downgrade FREE
 *  - Not dismissible — partner must update payment method to remove it
 */
@Component({
    selector: 'payment-failed-banner',
    templateUrl: './payment-failed-banner.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        TranslocoModule
    ]
})
export class PaymentFailedBannerComponent implements OnInit, OnDestroy {
    visible: boolean = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _router: Router,
        private _subscriptionService: SubscriptionService,
        private _cdr: ChangeDetectorRef
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this._subscriptionService.partnerSubscription$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((subscription: Subscription) => {
                this.visible = !!subscription
                    && subscription.subscriptionStatusRefCode === SubscriptionStatusEnum.PIN;
                this._cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Navigate to the subscription/billing settings page so the partner
     * can update their payment method.
     */
    navigateToSubscription(): void {
        this._router.navigate([
            `/${PathEnum.APP}/${PathEnum.APP_PARTNER_SETTINGS}/${PathEnum.APP_PARTNER_SETTINGS_SUBSCRIPTION}`
        ]);
    }
}
